/** Phase 5F — SCR-MER-043–049 finance bindings (07-02) */

export const FINANCE_SCREEN_IDS = [
  'SCR-MER-043',
  'SCR-MER-044',
  'SCR-MER-045',
  'SCR-MER-046',
  'SCR-MER-047',
  'SCR-MER-048',
  'SCR-MER-049',
  'SCR-MER-102',
  'finance-transactions',
  'finance-reports',
] as const;

export type FinanceScreenId = (typeof FINANCE_SCREEN_IDS)[number];

export const FINANCE_ROUTES: Record<FinanceScreenId, string> = {
  'SCR-MER-043': '/finance-overview',
  'SCR-MER-044': '/order-earnings',
  'SCR-MER-045': '/statements',
  'SCR-MER-046': '/statement-detail',
  'SCR-MER-047': '/payouts',
  'SCR-MER-048': '/instant-payout',
  'SCR-MER-049': '/deductions-and-disputes',
  'SCR-MER-102': '/finance/beat-pay',
  'finance-transactions': '/finance/transactions',
  'finance-reports': '/finance/reports',
};

export const FINANCE_PERMISSIONS: Partial<Record<FinanceScreenId, readonly string[]>> = {
  'SCR-MER-043': ['PERM-settlement.balance.read'],
  'SCR-MER-044': ['PERM-settlement.statement.read'],
  'SCR-MER-045': ['PERM-settlement.statement.read'],
  'SCR-MER-046': ['PERM-settlement.statement.read', 'PERM-settlement.statement.export'],
  'SCR-MER-047': ['PERM-payout.payout.read', 'PERM-settlement.statement.read'],
  'SCR-MER-048': ['PERM-payout.instant.request', 'PERM-settlement.payout.request-instant'],
  'SCR-MER-049': [
    'PERM-settlement.deduction.read',
    'PERM-dispute.dispute.read',
    'PERM-dispute.dispute.create',
    'PERM-dispute.dispute.respond',
  ],
  'SCR-MER-102': ['PERM-beatpay.onboarding.manage'],
  'finance-transactions': ['PERM-payment.transaction.list', 'PERM-payment.transaction.read'],
  'finance-reports': ['PERM-settlement.statement.read'],
};

/** Live merchant API / seed codes (10-05b route matrix). Inventory aliases are above. */
export const FINANCE_LIVE_PERMS = {
  overviewRead: 'PERM-settlement.balance.read',
  statementRead: 'PERM-settlement.statement.read',
  statementExport: 'PERM-settlement.statement.export',
  payoutRead: 'PERM-settlement.statement.read',
  instantRequest: 'PERM-settlement.payout.request-instant',
  deductionRead: 'PERM-settlement.deduction.read',
  disputeRead: 'PERM-dispute.dispute.read',
  disputeCreate: 'PERM-dispute.dispute.create',
  disputeRespond: 'PERM-dispute.dispute.respond',
  invoiceList: 'PERM-invoice.invoice.list',
  destinationRead: 'PERM-payout.destination.read',
} as const;

export const FINANCE_ENDPOINTS: Partial<Record<FinanceScreenId, readonly string[]>> = {
  'SCR-MER-043': [
    'GET /merchant/v1/finance/summary',
    'GET /merchant/v1/finance/balance',
    'GET /merchant/v1/finance/statements',
    'GET /merchant/v1/finance/adjustments',
    'GET /merchant/v1/onboarding/beat-pay/status',
  ],
  'SCR-MER-044': ['GET /merchant/v1/finance/orders/{orderId}/breakdown'],
  'SCR-MER-045': ['GET /merchant/v1/finance/statements'],
  'SCR-MER-046': [
    'GET /merchant/v1/finance/statements/{id}',
    'POST /merchant/v1/finance/statements/{id}/exports',
  ],
  'SCR-MER-047': [
    'GET /merchant/v1/finance/payouts',
    'GET /merchant/v1/finance/payouts/{id}',
    'GET /merchant/v1/finance/summary',
    'GET /merchant/v1/finance/payout-destination',
    'GET /merchant/v1/finance/adjustments',
  ],
  'SCR-MER-048': [
    'GET /merchant/v1/finance/summary',
    'POST /merchant/v1/finance/payouts/instant',
    'POST /merchant/v1/finance/payouts/next-day',
  ],
  'SCR-MER-049': [
    'GET /merchant/v1/finance/adjustments',
    'GET /merchant/v1/disputes',
    'POST /merchant/v1/disputes',
    'POST /merchant/v1/disputes/{id}/evidence',
    'POST /merchant/v1/disputes/{id}/accept',
  ],
  'SCR-MER-102': [
    'GET /merchant/v1/onboarding/beat-pay/status',
    'POST /merchant/v1/onboarding/beat-pay/start',
    'POST /merchant/v1/onboarding/beat-pay/link',
    'POST /merchant/v1/onboarding/beat-pay/refresh',
  ],
  'finance-transactions': ['POST /merchant/v1/finance/transactions/component-session'],
  'finance-reports': [
    'GET /merchant/v1/onboarding/beat-pay/status',
    'POST /merchant/v1/finance/reports/component-session',
  ],
};

export const ORDER_ID_STORAGE_KEY = 'beatq.merchant.orderId';
export const STATEMENT_ID_STORAGE_KEY = 'beatq.merchant.statementId';
export const PAYOUT_ID_STORAGE_KEY = 'beatq.merchant.payoutId';
export const DISPUTE_ID_STORAGE_KEY = 'beatq.merchant.disputeId';
